/**
 * @cafe-os/db — super-admin CRM (00102) + Finance summary (00103).
 */
import { callRpcWithDefaults } from "./errors";

export type LeadStage = "new" | "demo" | "trial" | "won" | "lost";
export type LeadSource = "telegram" | "landing" | "manual" | "phone" | "referral" | "other";

export interface LeadRow {
  id: string;
  tenant_name: string;
  owner_name: string | null;
  phone: string | null;
  city: string | null;
  source: LeadSource;
  stage: LeadStage;
  note: string | null;
  value: number;
  demo_date: string | null;
  trial_ends_at: string | null;
  lost_reason: string | null;
  hot: boolean;
  risk: boolean;
  vip: boolean;
  assigned_to: string | null;
  last_contact_at: string | null;
  converted_tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLeadInput {
  tenant_name: string;
  owner_name?: string | null;
  phone?: string | null;
  city?: string | null;
  source?: LeadSource;
  stage?: LeadStage;
  note?: string | null;
  value?: number;
  hot?: boolean;
  risk?: boolean;
  vip?: boolean;
  demo_date?: string | null;
  trial_ends_at?: string | null;
  lost_reason?: string | null;
  assigned_to?: string | null;
}

export async function superAdminCrmListLeads(): Promise<LeadRow[]> {
  const data = await callRpcWithDefaults<LeadRow[]>("super_admin_crm_list_leads", {});
  return Array.isArray(data) ? data : [];
}

export async function superAdminCrmCreateLead(input: CreateLeadInput): Promise<LeadRow> {
  const data = await callRpcWithDefaults<LeadRow>("super_admin_crm_create_lead", { p: input });
  if (!data) throw new Error("[db/superAdminCrmCreateLead] empty");
  return data;
}

export async function superAdminCrmUpdateLead(id: string, patch: Partial<CreateLeadInput>): Promise<LeadRow> {
  const data = await callRpcWithDefaults<LeadRow>("super_admin_crm_update_lead", {
    p_id: id, p: patch,
  });
  if (!data) throw new Error("[db/superAdminCrmUpdateLead] empty");
  return data;
}

export async function superAdminCrmMoveStage(id: string, stage: LeadStage): Promise<LeadRow> {
  const data = await callRpcWithDefaults<LeadRow>("super_admin_crm_move_stage", {
    p_id: id, p_stage: stage,
  });
  if (!data) throw new Error("[db/superAdminCrmMoveStage] empty");
  return data;
}

export async function superAdminCrmDeleteLead(id: string): Promise<void> {
  await callRpcWithDefaults("super_admin_crm_delete_lead", { p_id: id });
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk CSV import (00110)
// ─────────────────────────────────────────────────────────────────────────────

export interface BulkLeadsFailedRow {
  row: number;
  reason: string;
}
export interface BulkLeadsSkippedDup {
  row: number;
  phone: string;
  existing_lead_id: string;
}
export interface BulkLeadsResult {
  inserted: number;
  failed: BulkLeadsFailedRow[];
  skipped_dup: BulkLeadsSkippedDup[];
}

/**
 * 00110 — bulk insert array of CreateLeadInput с per-row валидацией +
 * dedup по phone (digits-only match). Возвращает структурированный отчёт:
 * inserted count + failed rows (с reason) + skipped duplicates.
 *
 * Caller сам отображает результат — UI обычно показывает:
 *   "{inserted} импортировано, {failed.length} ошибок, {skipped_dup.length} дубликатов"
 */
export async function superAdminCrmBulkCreateLeads(
  input: CreateLeadInput[],
): Promise<BulkLeadsResult> {
  const data = await callRpcWithDefaults<BulkLeadsResult>(
    "super_admin_crm_bulk_create_leads",
    { p_leads: input },
  );
  if (!data) throw new Error("[superAdminCrmBulkCreateLeads] empty response");
  return data;
}

export interface ConvertLeadResult {
  ok: boolean;
  lead_id: string;
  tenant_id: string;
  previous_stage: LeadStage;
}

/**
 * 00108 — link lead → tenant + auto stage='won' + audit + Telegram outbox event.
 * Throws "lead_already_converted" если уже сконвертирован
 * или "tenant_not_found" если tenant не существует.
 */
export async function superAdminCrmConvertLead(
  leadId: string,
  tenantId: string,
): Promise<ConvertLeadResult> {
  const data = await callRpcWithDefaults<ConvertLeadResult>(
    "super_admin_crm_convert_lead_to_tenant",
    { p_lead_id: leadId, p_tenant_id: tenantId },
  );
  if (!data) throw new Error("[superAdminCrmConvertLead] empty response");
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lead activity timeline + tasks (00297) — лента взаимодействий по лиду
// ─────────────────────────────────────────────────────────────────────────────

export type LeadActivityKind = "note" | "call" | "meeting" | "task" | "stage_change";

export interface LeadActivity {
  id: string;
  lead_id: string;
  kind: LeadActivityKind;
  body: string | null;
  /** Дедлайн напоминания (только kind='task'). */
  due_at: string | null;
  done: boolean;
  done_at: string | null;
  author_email: string | null;
  created_at: string;
}

export interface OpenTask {
  id: string;
  lead_id: string;
  lead_name: string;
  body: string | null;
  due_at: string | null;
  created_at: string;
}

export async function superAdminCrmListActivities(leadId: string): Promise<LeadActivity[]> {
  const data = await callRpcWithDefaults<LeadActivity[]>("super_admin_crm_list_activities", {
    p_lead_id: leadId,
  });
  return Array.isArray(data) ? data : [];
}

export async function superAdminCrmAddActivity(input: {
  leadId: string;
  kind: "note" | "call" | "meeting" | "task";
  body?: string | null;
  /** ISO timestamp дедлайна — только для kind='task'. */
  dueAt?: string | null;
}): Promise<LeadActivity> {
  const data = await callRpcWithDefaults<LeadActivity>("super_admin_crm_add_activity", {
    p_lead_id: input.leadId,
    p_kind: input.kind,
    p_body: input.body ?? null,
    p_due_at: input.dueAt ?? null,
  });
  if (!data) throw new Error("[db/superAdminCrmAddActivity] empty");
  return data;
}

export async function superAdminCrmToggleTask(id: string, done: boolean): Promise<LeadActivity> {
  const data = await callRpcWithDefaults<LeadActivity>("super_admin_crm_toggle_task", {
    p_id: id,
    p_done: done,
  });
  if (!data) throw new Error("[db/superAdminCrmToggleTask] empty");
  return data;
}

export async function superAdminCrmDeleteActivity(id: string): Promise<void> {
  await callRpcWithDefaults("super_admin_crm_delete_activity", { p_id: id });
}

export async function superAdminCrmOpenTasks(): Promise<OpenTask[]> {
  const data = await callRpcWithDefaults<OpenTask[]>("super_admin_crm_open_tasks", {});
  return Array.isArray(data) ? data : [];
}

// ─────────────────────────────────────────────────────────────────────────────
// Finance summary (00103)
// ─────────────────────────────────────────────────────────────────────────────

export interface FinanceTransaction {
  id: string;
  tenant_id: string;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "failed" | "refunded" | "void";
  period_start: string;
  period_end: string;
  paid_at: string | null;
  created_at: string;
}

export interface FinanceSummary {
  mrr_kzt: number;
  arr_kzt: number;
  active_subscriptions: number;
  paid_invoices_count: number;
  paid_invoices_total_kzt: number;
  avg_invoice_kzt: number;
  churned_period: number;
  churn_rate_pct: number;
  ltv_kzt: number;
  period_days: number;
  transactions: FinanceTransaction[];
}

export async function superAdminFinanceSummary(periodDays = 30): Promise<FinanceSummary> {
  const data = await callRpcWithDefaults<FinanceSummary>(
    "super_admin_finance_summary",
    { p_period_days: periodDays },
  );
  if (!data) throw new Error("[db/superAdminFinanceSummary] empty");
  return data;
}
